import { lazy, Suspense } from "react";
import { RouteObject, Navigate } from "react-router-dom";
import { AppErrorBoundary } from "@/components/providers/AppErrorBoundary";
import { PublicShellLazy, HomeLazy, BlogIndexLazy, BlogPostLazy } from "@/lib/prerenderedRoutes";
import { DashboardProviders } from "@/components/providers/DashboardProviders";
import { HiveRouteGate } from "@/components/workforce-hive/HiveRouteGate";

// Lazy load all pages for optimal code splitting.
// Home is lazy too — a <link rel="modulepreload"> for the Home chunk is
// injected into the prerendered "/" HTML by scripts/prerender-marketing.mjs
// so the chunk downloads in parallel with the entry bundle.
// PublicShell is also lazy — the shell (Header + Footer + persona/menu code)
// is the biggest synchronous tail still inside the entry bundle. Splitting
// it out shrinks the critical-path JS materially. A <link rel="modulepreload">
// for the PublicShell chunk is injected on every marketing route by the
// prerender script so the chunk loads in parallel with the entry on hard
// refresh (no visible Suspense flash).
const PublicShell = PublicShellLazy;
const Index = HomeLazy;
const Auth = lazy(() => import("@/pages/Auth"));
const About = lazy(() => import("@/pages/About"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Contact = lazy(() => import("@/pages/Contact"));
const Demo = lazy(() => import("@/pages/Demo"));
const Employers = lazy(() => import("@/pages/Employers"));
const Agencies = lazy(() => import("@/pages/Agencies"));
const Temps = lazy(() => import("@/pages/Temps"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const Careers = lazy(() => import("@/pages/Careers"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Status = lazy(() => import("@/pages/Status"));
const NewsletterUnsubscribe = lazy(() => import("@/pages/NewsletterUnsubscribe"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const CreditPurchaseSuccess = lazy(() => import("@/pages/CreditPurchaseSuccess"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Waitlist = lazy(() => import("@/pages/Waitlist"));

// Workforce Hive (Phase 1 frontend scaffold)
const WorkforceHivePage = lazy(() => import("@/features/workforce-hive/pages/WorkforceHivePage"));
const HiveClaimDetailsPage = lazy(() => import("@/features/workforce-hive/pages/HiveClaimDetailsPage"));
const HiveCheckoutReviewPage = lazy(() => import("@/features/workforce-hive/pages/HiveCheckoutReviewPage"));
const HiveCheckoutSuccessPage = lazy(() => import("@/features/workforce-hive/pages/HiveCheckoutSuccessPage"));
const HiveCheckoutCancelledPage = lazy(() => import("@/features/workforce-hive/pages/HiveCheckoutCancelledPage"));
const HiveCheckoutReservationPage = lazy(() => import("@/features/workforce-hive/pages/HiveCheckoutReservationPage"));
const HiveSponsorPreviewPage = lazy(() => import("@/features/workforce-hive/pages/HiveSponsorPreviewPage"));
const HiveInterestPage = lazy(() => import("@/features/workforce-hive/pages/HiveInterestPage"));

// Public blog (Phase 2)
const BlogIndex = BlogIndexLazy;
const BlogPost = BlogPostLazy;

// Tools pages
const ToolsIndex = lazy(() => import("@/pages/tools/index"));
const ChargeRateCalculator = lazy(() => import("@/pages/tools/ChargeRateCalculator"));
const HolidayPayCalculator = lazy(() => import("@/pages/tools/HolidayPayCalculator"));
const HourlyToAnnualCalculator = lazy(() => import("@/pages/tools/HourlyToAnnualCalculator"));


// Lazy load feature pages
const EmployerShiftManagement = lazy(() => import("@/pages/employers/ShiftManagement"));
const EmployerStaffManagement = lazy(() => import("@/pages/employers/StaffManagement"));
const EmployerBudgetReporting = lazy(() => import("@/pages/employers/BudgetReporting"));
const EmployerAdvanced = lazy(() => import("@/pages/employers/Advanced"));
const AgencyWorkerManagement = lazy(() => import("@/pages/agencies/WorkerManagement"));
const AgencyEmployerPortal = lazy(() => import("@/pages/agencies/EmployerPortal"));
const AgencyShiftMatching = lazy(() => import("@/pages/agencies/ShiftMatching"));
const AgencyOperations = lazy(() => import("@/pages/agencies/Operations"));
const TempShiftDiscovery = lazy(() => import("@/pages/temps/ShiftDiscovery"));
const TempSchedule = lazy(() => import("@/pages/temps/Schedule"));
const TempEarnings = lazy(() => import("@/pages/temps/Earnings"));
const TempCareer = lazy(() => import("@/pages/temps/Career"));

// Route configuration with breadcrumb metadata
export const routes: RouteObject[] = [
  // Public routes with persistent shell (Header/Footer never remount)
  {
    element: (
      <Suspense fallback={null}>
        <PublicShell />
      </Suspense>
    ),
    children: [
      { path: "/", element: <Index /> },
      { 
        path: "/about", 
        element: <About />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "About" }] }
      },
      { 
        path: "/pricing", 
        element: <Pricing />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Pricing" }] }
      },
      { 
        path: "/contact", 
        element: <Contact />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Contact" }] }
      },
      { 
        path: "/demo", 
        element: <Demo />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Request Demo" }] }
      },
      
      { 
        path: "/waitlist", 
        element: <Waitlist />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Waitlist" }] }
      },
      { path: "/subscription-success", element: <SubscriptionSuccess /> },
      { path: "/credits-purchase-success", element: <CreditPurchaseSuccess /> },

      // Workforce Hive (public) — runtime gated. Routes are always
      // registered; <HiveRouteGate> reads the platform_settings runtime
      // flag for the current environment and either renders the Hive
      // page, a neutral loading skeleton, or <NotFound /> when disabled
      // / fail-closed. No rebuild required to enable or disable.
      {
        path: "/workforce-hive",
        element: <HiveRouteGate><WorkforceHivePage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive" }] },
      },
      {
        path: "/workforce-hive/claim",
        element: <HiveRouteGate><HiveClaimDetailsPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Claim interest" }] },
      },
      {
        path: "/workforce-hive/interest",
        element: <HiveRouteGate><HiveInterestPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Register interest" }] },
      },
      {
        path: "/workforce-hive/checkout",
        element: <HiveRouteGate><HiveCheckoutReviewPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Review" }] },
      },
      {
        path: "/workforce-hive/success",
        element: <HiveRouteGate><HiveCheckoutSuccessPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Submitted" }] },
      },
      {
        path: "/workforce-hive/cancelled",
        element: <HiveRouteGate><HiveCheckoutCancelledPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Cancelled" }] },
      },
      {
        path: "/workforce-hive/reserved",
        element: <HiveRouteGate><HiveCheckoutReservationPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Reserved" }] },
      },
      {
        path: "/workforce-hive/sponsor-preview",
        element: <HiveRouteGate><HiveSponsorPreviewPage /></HiveRouteGate>,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Workforce Hive", href: "/workforce-hive" }, { label: "Sponsor preview" }] },
      },
      
      // Public blog (Phase 2)
      {
        path: "/blog",
        element: <BlogIndex />,
        handle: {
          breadcrumbs: [{ label: "Home", href: "/" }, { label: "Blog" }],
          breadcrumbsJsonLd: false,
        },
      },
      {
        // Path-based archive pages. Same component as `/blog` — it reads the
        // `:page` route param to determine the current archive page. The
        // literal `page` segment outranks the `/blog/:slug` route in React
        // Router's static-vs-dynamic resolution.
        path: "/blog/page/:page",
        element: <BlogIndex />,
        handle: {
          breadcrumbs: [{ label: "Home", href: "/" }, { label: "Blog" }],
          breadcrumbsJsonLd: false,
        },
      },
      {
        path: "/blog/:slug",
        element: <BlogPost />,
        handle: {
          breadcrumbs: [
            { label: "Home", href: "/" },
            { label: "Blog", href: "/blog/" },
            { label: "Post" },
          ],
          breadcrumbsJsonLd: false,
        },
      },

      // Tools routes
      { 
        path: "/tools", 
        element: <ToolsIndex />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Tools" }] }
      },
      { 
        path: "/tools/charge-rate-calculator", 
        element: <ChargeRateCalculator />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Tools", href: "/tools/" }, { label: "Charge Rate Calculator" }] }
      },
      {
        path: "/tools/holiday-pay-calculator",
        element: <HolidayPayCalculator />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Tools", href: "/tools/" }, { label: "Holiday Pay Calculator" }] }
      },
      {
        path: "/tools/hourly-to-annual-calculator",
        element: <HourlyToAnnualCalculator />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Tools", href: "/tools/" }, { label: "Hourly to Annual Salary Calculator" }] }
      },

      
      // Employer routes
      { 
        path: "/employers", 
        element: <Employers />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Employers" }] }
      },
      { 
        path: "/employers/features", 
        element: <Navigate to="/employers/" replace />
      },
      { 
        path: "/employers/features/shift-management", 
        element: <EmployerShiftManagement />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Employers", href: "/employers/" }, { label: "Shift Management" }] }
      },
      { 
        path: "/employers/features/staff-management", 
        element: <EmployerStaffManagement />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Employers", href: "/employers/" }, { label: "Staff Management" }] }
      },
      { 
        path: "/employers/features/budget-reporting", 
        element: <EmployerBudgetReporting />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Employers", href: "/employers/" }, { label: "Budget & Reporting" }] }
      },
      { 
        path: "/employers/features/advanced", 
        element: <EmployerAdvanced />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Employers", href: "/employers/" }, { label: "Advanced Features" }] }
      },
      
      // Agency routes
      { 
        path: "/agencies", 
        element: <Agencies />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Agencies" }] }
      },
      { 
        path: "/agencies/features", 
        element: <Navigate to="/agencies/" replace />
      },
      { 
        path: "/agencies/features/worker-management", 
        element: <AgencyWorkerManagement />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Agencies", href: "/agencies/" }, { label: "Worker Management" }] }
      },
      { 
        path: "/agencies/features/employer-portal", 
        element: <AgencyEmployerPortal />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Agencies", href: "/agencies/" }, { label: "Employer Portal" }] }
      },
      { 
        path: "/agencies/features/shift-matching", 
        element: <AgencyShiftMatching />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Agencies", href: "/agencies/" }, { label: "Shift Matching" }] }
      },
      { 
        path: "/agencies/features/operations", 
        element: <AgencyOperations />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Agencies", href: "/agencies/" }, { label: "Operations" }] }
      },
      
      // Temp routes
      { 
        path: "/temps", 
        element: <Temps />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Temps" }] }
      },
      { 
        path: "/temps/features", 
        element: <Navigate to="/temps/" replace />
      },
      { 
        path: "/temps/features/shift-discovery", 
        element: <TempShiftDiscovery />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Temps", href: "/temps/" }, { label: "Shift Discovery" }] }
      },
      { 
        path: "/temps/features/schedule", 
        element: <TempSchedule />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Temps", href: "/temps/" }, { label: "Schedule" }] }
      },
      { 
        path: "/temps/features/earnings", 
        element: <TempEarnings />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Temps", href: "/temps/" }, { label: "Earnings" }] }
      },
      { 
        path: "/temps/features/career", 
        element: <TempCareer />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Temps", href: "/temps/" }, { label: "Career" }] }
      },
      
      // Static/Legal pages
      { 
        path: "/privacy-policy", 
        element: <PrivacyPolicy />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Privacy Policy" }] }
      },
      { 
        path: "/terms-of-service", 
        element: <TermsOfService />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Terms of Service" }] }
      },
      { 
        path: "/sitemap", 
        element: <Sitemap />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Sitemap" }] }
      },
      { 
        path: "/careers", 
        element: <Careers />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Careers" }] }
      },
      { 
        path: "/help", 
        element: <HelpCenter />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Help Center" }] }
      },
      { 
        path: "/status", 
        element: <Status />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Status" }] }
      },
      { 
        path: "/newsletter/unsubscribe", 
        element: <NewsletterUnsubscribe />,
        handle: { breadcrumbs: [{ label: "Home", href: "/" }, { label: "Unsubscribe" }] }
      },
      { path: "/credits", element: <Navigate to="/dashboard/billing" replace /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  
  // Auth routes - standalone, no shell
  { path: "/auth", element: <Auth /> },
  { path: "/auth/reset-password", element: <ResetPassword /> },
  
  // Dashboard routes - lazy loaded, splash loader covers the gap.
  // Phase 1 — Dashboard-only providers (BrandingStyleInjector) are mounted
  // here so marketing routes don't pay for tenant-branding CSS injection.
  {
    path: "/dashboard",
    element: <DashboardProviders><Dashboard /></DashboardProviders>,
    errorElement: <AppErrorBoundary><></></AppErrorBoundary>,
  },
  {
    path: "/dashboard/*",
    element: <DashboardProviders><Dashboard /></DashboardProviders>,
    errorElement: <AppErrorBoundary><></></AppErrorBoundary>,
  },
];
